import { useState, useCallback, useEffect } from 'react'
import { IBuilderLibraries, IStupidEdiConfigs, Maybe } from 'types/graphqlTypes'
import { useActivateConfigurableBuilderVersionMutation } from '../graphql/ActivateConfigurableBuilderVersionMutation'
import { useConfigurableBuilderVersionsQuery } from '../graphql/ConfigurableBuilderVersions'
import { useCreateConfigurableBuilderVersionMutation } from '../graphql/CreateConfigurableBuilderVersionMutation'
import { useUpdateConfigurableBuilderVersionMutation } from '../graphql/UpdateConfigurableBuilderVersionMutation'
import { IConfigurableBuilderVersionSchema } from '../schema/ConfigurableBuilderVersionSchema'

interface IUseConfigurableBuilderVersionArgs {
  builderId: string
  displayUserFeedback: (message: string, level?: 'error' | 'info') => void
  builderLibraryChangeHandler: (version: IBuilderLibraries) => void
  stupidediConfigChangeHandler: (version: Maybe<IStupidEdiConfigs>) => void
  tfx12DefinitionIdChangeHandler: (version: Maybe<string>) => void
  canBuildBalanceDuesChangeHandler: (version: boolean) => void
}

export const useConfigurableBuilderVersion = ({
  builderId,
  builderLibraryChangeHandler,
  canBuildBalanceDuesChangeHandler,
  displayUserFeedback,
  stupidediConfigChangeHandler,
  tfx12DefinitionIdChangeHandler,
}: IUseConfigurableBuilderVersionArgs) => {
  const [
    selectedConfigurableBuilderVersion,
    setSelectedConfigurableBuilderVersion,
  ] = useState<IConfigurableBuilderVersionSchema | null>(null)

  const [configurableBuilderVersions, setConfigurableBuilderVersions] = useState<
    IConfigurableBuilderVersionSchema[]
  >([])

  const {
    data: versionsData,
    loading: isLoadingVersions,
    refetch: refetchVersions,
  } = useConfigurableBuilderVersionsQuery({
    variables: {
      id: builderId,
    },
  })
  const [
    createConfigurableBuilderVersionMutation,
    { loading: isLoadingCreateMutation },
  ] = useCreateConfigurableBuilderVersionMutation()
  const [
    updateConfigurableBuilderVersionMutation,
    { loading: isLoadingUpdateMutation },
  ] = useUpdateConfigurableBuilderVersionMutation()
  const [
    activateConfigurableBuilderVersionMutation,
  ] = useActivateConfigurableBuilderVersionMutation()

  const activateConfigurableBuilderVersion = useCallback(
    async (versionId: string, outboundEdiMapperConnectionConfigId: string) => {
      try {
        const response = await activateConfigurableBuilderVersionMutation({
          variables: {
            input: {
              id: versionId,
              outboundConfigId: outboundEdiMapperConnectionConfigId,
            },
          },
        })
        const responseData = response.data?.activateConfigurableBuilderVersion

        let errors = response.data?.activateConfigurableBuilderVersion?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length === 0) {
          if (
            responseData?.configurableBuilderVersion &&
            responseData?.configurableBuilderVersion?.isLive
          ) {
            displayUserFeedback(
              `Version ${responseData.configurableBuilderVersion.id} is now active.`,
              'info'
            )
          } else {
            displayUserFeedback(
              'Version was not activated, but no errors were returned from the server.',
              'error'
            )
          }
          refetchVersions()
        } else {
          displayUserFeedback(errors[0].message, 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activateConfigurableBuilderVersionMutation]
  )

  const createConfigurableBuilderVersion = useCallback(
    async (version: IConfigurableBuilderVersionSchema) => {
      try {
        const response = await createConfigurableBuilderVersionMutation({
          variables: {
            input: {
              ediMapperConfigurableBuilderId: version.ediMapperConfigurableBuilderId,
              extractionTemplate: version.extractionTemplate,
              builderLibrary: version.builderLibrary,
              stupidediConfig: version.stupidediConfig,
              tfx12DefinitionId: version.tfx12DefinitionId,
              canBuildBalanceDues: version.canBuildBalanceDues,
            },
          },
        })
        let errors = response.data?.createConfigurableBuilderVersion?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length === 0) {
          refetchVersions()
        } else {
          displayUserFeedback(errors[0].message, 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createConfigurableBuilderVersionMutation]
  )

  const updateConfigurableBuilderVersion = useCallback(
    async (version: IConfigurableBuilderVersionSchema) => {
      try {
        const response = await updateConfigurableBuilderVersionMutation({
          variables: {
            input: {
              id: version.id,
              extractionTemplate: version.extractionTemplate,
              builderLibrary: version.builderLibrary,
              stupidediConfig: version.stupidediConfig,
              tfx12DefinitionId: version.tfx12DefinitionId,
              canBuildBalanceDues: version.canBuildBalanceDues,
            },
          },
        })
        let errors = response.data?.updateConfigurableBuilderVersion?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length === 0) {
          refetchVersions()
          builderLibraryChangeHandler(version.builderLibrary)
          stupidediConfigChangeHandler(version.stupidediConfig)
          tfx12DefinitionIdChangeHandler(version.tfx12DefinitionId)
          canBuildBalanceDuesChangeHandler(version.canBuildBalanceDues)
        } else {
          displayUserFeedback(errors[0].message, 'error')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateConfigurableBuilderVersionMutation]
  )

  useEffect(() => {
    const versions =
      versionsData?.configurableBuilderVersions?.map(version => ({
        ...version,
        ediMapperConfigurableBuilderId: builderId,
      })) || []
    setConfigurableBuilderVersions(versions)
  }, [builderId, versionsData])

  // when loading new versions (like when refreshing versions after making a version live)
  // we should update what's in selectedConfigurableBuilderVersion
  useEffect(() => {
    if (selectedConfigurableBuilderVersion) {
      const updated = configurableBuilderVersions.find(
        elem => elem.id === selectedConfigurableBuilderVersion.id
      )
      if (updated) {
        setSelectedConfigurableBuilderVersion(updated)
      }
    }
  }, [configurableBuilderVersions, selectedConfigurableBuilderVersion])

  return {
    activateConfigurableBuilderVersion,
    configurableBuilderVersions,
    createConfigurableBuilderVersion,
    isLoading: isLoadingVersions || isLoadingCreateMutation || isLoadingUpdateMutation,
    selectedConfigurableBuilderVersion,
    setSelectedConfigurableBuilderVersion,
    updateConfigurableBuilderVersion,
  }
}
